import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { api } from "../lib/api";
import { useAuth } from "./AuthContext";

/**
 * LearningContext — the single source of truth for everything the user
 * collects across the app: highlights, notes, bookmarks. Mirrors the server
 * state in memory so every screen (Reader, VerseOverlay, BeitMidrash hub,
 * Highlights index) reads from the SAME store and updates instantly.
 *
 * Architecture:
 *   • On login (or first mount with a logged-in user) we fetch all three
 *     collections in parallel and cache them.
 *   • Mutations call the server first then update the local cache so any
 *     screen using the corresponding hook re-renders within one tick.
 *   • Lookups (`getHighlight`, `getNote`, etc.) are O(1) via Maps keyed on
 *     `<book>:<chapter>:<verse>` so the Reader can paint highlight colors
 *     directly inside the 3D book without re-fetching anything.
 *
 * The basket already lives in its own BasketContext (also localStorage-
 * backed). We keep them separate because the basket is a *staging* area
 * (frequent local edits, no server round-trip needed) while highlights/
 * notes/bookmarks are *committed* learning artefacts that must round-trip.
 */

const LearningContext = createContext(null);

const keyOf = (b, c, v) => `${b}:${c}:${v}`;

export function LearningProvider({ children }) {
  const { user, loading: authLoading } = useAuth();

  const [highlights, setHighlights] = useState([]);
  const [notes, setNotes] = useState([]);
  const [bookmarks, setBookmarks] = useState([]);
  const [loaded, setLoaded] = useState(false);

  // Avoid duplicate fetches when several screens mount in quick succession
  const inflight = useRef(false);

  const reload = useCallback(async () => {
    if (!user || inflight.current) return;
    inflight.current = true;
    try {
      const [hl, ns, bm] = await Promise.all([
        api.highlights().catch(() => []),
        api.notes().catch(() => []),
        api.bookmarks().catch(() => []),
      ]);
      // Backend returns each as a List directly (no wrapper object)
      setHighlights(Array.isArray(hl) ? hl : hl.highlights || []);
      setNotes(Array.isArray(ns) ? ns : ns.notes || []);
      setBookmarks(Array.isArray(bm) ? bm : bm.bookmarks || []);
      setLoaded(true);
    } finally {
      inflight.current = false;
    }
  }, [user]);

  useEffect(() => {
    if (authLoading) return;
    if (!user) {
      // Logged out → clear caches so the next user doesn't see prior data.
      setHighlights([]);
      setNotes([]);
      setBookmarks([]);
      setLoaded(false);
      return;
    }
    reload();
  }, [user, authLoading, reload]);

  // ---- Indexes (memoised) ----
  const highlightIndex = useMemo(() => {
    const m = new Map();
    for (const h of highlights) m.set(keyOf(h.book, h.chapter, h.verse), h);
    return m;
  }, [highlights]);

  const noteIndex = useMemo(() => {
    const m = new Map();
    for (const n of notes) m.set(keyOf(n.book, n.chapter, n.verse), n);
    return m;
  }, [notes]);

  const bookmarkIndex = useMemo(() => {
    const m = new Map();
    for (const b of bookmarks) m.set(keyOf(b.book, b.chapter, b.verse), b);
    return m;
  }, [bookmarks]);

  // ---- Mutations ----
  const upsertHighlight = useCallback(
    async ({ book, chapter, verse, color, reason }) => {
      const created = await api.addHighlight({
        book,
        chapter,
        verse,
        color,
        reason,
      });
      // Server upserts by (user, book, ch, v); replace any prior local entry.
      setHighlights((prev) => {
        const without = prev.filter(
          (h) =>
            !(h.book === created.book &&
              h.chapter === created.chapter &&
              h.verse === created.verse)
        );
        return [created, ...without];
      });
      return created;
    },
    []
  );

  const removeHighlight = useCallback(async (id) => {
    await api.delHighlight(id);
    setHighlights((prev) => prev.filter((h) => h.id !== id));
  }, []);

  const upsertNote = useCallback(
    async ({ book, chapter, verse, text }) => {
      const existing = noteIndex.get(keyOf(book, chapter, verse));
      let saved;
      if (existing) {
        saved = await api.updateNote(existing.id, text);
      } else {
        saved = await api.addNote({ book, chapter, verse, text });
      }
      setNotes((prev) => {
        const without = prev.filter((n) => n.id !== saved.id);
        return [saved, ...without];
      });
      return saved;
    },
    [noteIndex]
  );

  const removeNote = useCallback(async (id) => {
    await api.delNote(id);
    setNotes((prev) => prev.filter((n) => n.id !== id));
  }, []);

  const toggleBookmark = useCallback(
    async ({ book, chapter, verse, label }) => {
      const existing = bookmarkIndex.get(keyOf(book, chapter, verse));
      if (existing) {
        await api.delBookmark(existing.id);
        setBookmarks((prev) => prev.filter((b) => b.id !== existing.id));
        return null;
      }
      const created = await api.addBookmark({ book, chapter, verse, label });
      setBookmarks((prev) => [created, ...prev]);
      return created;
    },
    [bookmarkIndex]
  );

  const value = useMemo(
    () => ({
      // collections
      highlights,
      notes,
      bookmarks,
      loaded,
      // indexes for O(1) lookup
      getHighlight: (book, chapter, verse) =>
        highlightIndex.get(keyOf(book, chapter, verse)),
      getNote: (book, chapter, verse) =>
        noteIndex.get(keyOf(book, chapter, verse)),
      getBookmark: (book, chapter, verse) =>
        bookmarkIndex.get(keyOf(book, chapter, verse)),
      // chapter-scoped maps used by the Reader (verse number → object)
      highlightsByVerse: (book, chapter) => {
        const out = {};
        for (const h of highlights) {
          if (h.book === book && h.chapter === chapter) out[h.verse] = h;
        }
        return out;
      },
      notesByVerse: (book, chapter) => {
        const out = {};
        for (const n of notes) {
          if (n.book === book && n.chapter === chapter) out[n.verse] = n;
        }
        return out;
      },
      // mutations
      upsertHighlight,
      removeHighlight,
      upsertNote,
      removeNote,
      toggleBookmark,
      reload,
    }),
    [
      highlights,
      notes,
      bookmarks,
      loaded,
      highlightIndex,
      noteIndex,
      bookmarkIndex,
      upsertHighlight,
      removeHighlight,
      upsertNote,
      removeNote,
      toggleBookmark,
      reload,
    ]
  );

  return (
    <LearningContext.Provider value={value}>
      {children}
    </LearningContext.Provider>
  );
}

export function useLearning() {
  const ctx = useContext(LearningContext);
  if (!ctx) {
    // Safe fallback — if a non-provider tree mounts something, return inert
    // empty values so screens still render.
    return {
      highlights: [],
      notes: [],
      bookmarks: [],
      loaded: false,
      getHighlight: () => undefined,
      getNote: () => undefined,
      getBookmark: () => undefined,
      highlightsByVerse: () => ({}),
      notesByVerse: () => ({}),
      upsertHighlight: async () => {},
      removeHighlight: async () => {},
      upsertNote: async () => {},
      removeNote: async () => {},
      toggleBookmark: async () => {},
      reload: async () => {},
    };
  }
  return ctx;
}
